import { defineStore } from "pinia";

import type { MasterTemplate, TemplateType } from "hoxton-types";
import type { MasterTemplateFile } from "@/types/types";
import { MultiviewMode } from "@/enums/multiview";

export interface State {
    masterTemplates: Record<string, MasterTemplate>;
    masterTemplateIds: string[];
    uploadConfig: {
        clientId: string;
        config: { filePrefix: string; action: string; postData: string };
    } | null;
    viewMode: (typeof MultiviewMode)[keyof typeof MultiviewMode];
    recentlyUploadedFiles: Record<string, MasterTemplateFile>;

    //filters
    sizes: string[];
    types: string[];
    search: string;
    families: string[];
    hideResized: boolean;
}

export const useMasterTemplatesLibraryStore = defineStore("masterTemplatesLibrary", {
    state: (): State => ({
        masterTemplates: {},
        masterTemplateIds: [],
        uploadConfig: null,
        viewMode: MultiviewMode.Grid,
        recentlyUploadedFiles: {},

        //filters
        sizes: [],
        types: [],
        search: "",
        families: [],
        hideResized: false
    }),

    getters: {
        getSizeOptions: state => {
            return ({
                campaignMasterTemplates,
                campaignType
            }: {
                campaignMasterTemplates?: string[];
                campaignType?: TemplateType | null;
            }) => {
                const sizes = new Set<string>();

                const filteredTemplateIds = state.masterTemplateIds.filter(
                    id => !campaignMasterTemplates?.includes(id)
                );

                filteredTemplateIds.forEach(id => {
                    if (campaignType && state.masterTemplates[id]?.adType !== campaignType) {
                        return;
                    }

                    const template = state.masterTemplates[id];

                    if (template) {
                        sizes.add(`${template.width}x${template.height}`);
                    }
                });

                return Array.from(sizes);
            };
        },

        getFilteredMasterTemplateIds: state => {
            const searchLower = state.search.toLowerCase();
            const sizesSet = state.sizes.length > 0 ? new Set(state.sizes) : null;
            const typesSet = state.types.length > 0 ? new Set(state.types) : null;
            const familiesSet = state.families.length > 0 ? new Set(state.families) : null;

            return state.masterTemplateIds.filter(id => {
                const template = state.masterTemplates[id];

                if (!template) {
                    return false;
                }

                if (sizesSet && !sizesSet.has(`${template.width}x${template.height}`)) {
                    return false;
                }

                if (typesSet && !typesSet.has(template.adType)) {
                    return false;
                }

                if (familiesSet) {
                    const familyId = template.parentId ?? template._id;

                    if (!familiesSet.has(familyId)) {
                        return false;
                    }
                }

                if (!template.name.toLowerCase().includes(searchLower)) {
                    return false;
                }

                if (state.hideResized && template.parentId) {
                    return false;
                }

                return true;
            });
        },

        getTemplateFamilies: state => {
            const parentIds = new Set<string>();

            for (const templateId of state.masterTemplateIds) {
                const template = state.masterTemplates[templateId];

                if (!template?.parentId) {
                    continue;
                }

                parentIds.add(template.parentId);
            }

            return Array.from(parentIds).map(parentId => {
                const parent = state.masterTemplates[parentId];

                return {
                    value: parentId,
                    label: parent?.name
                };
            });
        },

        getTemplateFamilyIdsMatchingType: state => {
            const typesSet = state.types.length > 0 ? new Set(state.types) : null;

            if (!typesSet) {
                return null;
            }

            const matchingFamilyIds = new Set<string>();

            for (const templateId of state.masterTemplateIds) {
                const template = state.masterTemplates[templateId];

                if (template?.parentId && typesSet.has(template.adType)) {
                    matchingFamilyIds.add(template.parentId);
                }
            }

            return matchingFamilyIds;
        }
    },

    actions: {
        setMasterTemplates(templates: MasterTemplate[]) {
            this.masterTemplateIds = [];

            templates.forEach(template => {
                this.masterTemplates[template._id] = template;

                if (!this.masterTemplateIds.includes(template._id)) {
                    this.masterTemplateIds.push(template._id);
                }
            });
        },

        syncFilters(campaignTemplates?: string[] | null) {
            const validSizes = new Set<string>();
            const validFamilies = new Set<string>();

            this.masterTemplateIds.forEach(id => {
                const template = this.masterTemplates[id];

                if (template && !campaignTemplates?.includes(id) && this.types.includes(template.adType)) {
                    validSizes.add(`${template.width}x${template.height}`);

                    const familyId = template.parentId ?? template._id;
                    validFamilies.add(familyId);
                }
            });

            this.sizes = this.sizes.filter(size => validSizes.has(size));
            this.families = this.families.filter(family => validFamilies.has(family));
        },

        updateRecentlyUploadedFile(file: MasterTemplateFile) {
            const date = this.recentlyUploadedFiles[file.uid]?.date ?? new Date();
            this.recentlyUploadedFiles[file.uid] = { ...file, date };
        },

        clearRecentlyUploadedFiles() {
            this.recentlyUploadedFiles = {};
        },

        clearState() {
            this.masterTemplateIds = [];
            this.masterTemplates = {};
            this.uploadConfig = null;
            this.viewMode = MultiviewMode.Grid;
            this.recentlyUploadedFiles = {};

            this.sizes = [];
            this.types = [];
            this.search = "";
            this.families = [];
            this.hideResized = false;
        }
    }
});
